import ResourceTyped from 'Resources/ResourceTyped';
import extractApiError from 'Resources/extractApiError';

// Company-profile + page company/person extraction resource, split from the
// Assistant god resource (WEB-7958). Same URLs — pure client-side reorg.

export type CompanyProfile = {
	business: string;
	icp: string;
	competitors: string;
};

/** The payload also carries the admin's instructions for every agent (WEB-11923): same
 *  endpoint, but not part of the profile, and saved on their own. */
export type CompanyProfilePayload = CompanyProfile & { instructions: string; maxFieldLength: number };

export type PageCompanyMatch =
	| { status: 'none' }
	| { status: 'unknown' }
	| { status: 'existing'; clientId: number; clientName: string }
	| { status: 'new'; companyName: string; prospectingId: string; orgNumber: string | null };

export type PageCompanyContact = {
	name: string;
	role?: string;
	// The person's own employer, when the page states it. Drives the "Other people
	// found" grouping and which company they're added to.
	company?: string;
	email?: string;
	phone?: string;
	linkedin?: string;
	existsInCrm?: boolean;
	// Server-tagged: does this person work at the page's company?
	belongsToPage?: boolean;
};

export type PageCompanyOverrides = {
	name?: string;
	orgNumber?: string;
	phone?: string;
	street?: string;
	zip?: string;
	city?: string;
};

export type PageCompanyExtract = {
	company: {
		name: string;
		domain: string | null;
		orgNumber: string | null;
		phone: string | null;
		street: string | null;
		zip: string | null;
		city: string | null;
	} | null;
	contacts: PageCompanyContact[];
	prospecting: {
		prospectingId: string;
		name: string;
		domain?: string;
		city?: string;
		orgNumber?: string;
	} | null;
	/** `accountManagers` for a company already in the CRM (WEB-13771). */
	crm: { exists: boolean; clientId?: number; clientName?: string; accountManagers?: string[] } | null;
};

export type PagePersonCompany = {
	name: string;
	prospectingId?: string;
	domain?: string;
	orgNumber?: string | null;
	crm: { exists: boolean; clientId?: number; clientName?: string };
	/** The Upsales company's own about text; our company data has none (WEB-15186). */
	about?: string;
	employees?: number;
	industry?: string;
};

export type PagePersonExtract = {
	person: {
		name: string;
		title?: string;
		companyName?: string;
		linkedin?: string;
		existsInCrm?: boolean;
		contactId?: number;
		/** The contact's saved LinkedIn is this profile: a sure match, not a namesake. */
		sameProfile?: boolean;
	} | null;
	company: PagePersonCompany | null;
};

export type PagePersonCompanyOption = {
	clientId: number;
	name: string;
	orgNumber: string | null;
	domain: string | null;
	/** What tells apart a group's companies sharing one name (WEB-13771). */
	accountManagers?: string[];
};

export type PageCompanyAddResult = {
	clientId: number;
	clientName: string;
	companyCreated: boolean;
	contactsCreated: number;
	contactsSkipped: number;
	// The contact each sent contact became or matched, in the order sent; null if its save failed.
	contactIds: Array<number | null>;
};

class AssistantCompanyProfile extends ResourceTyped<CompanyProfilePayload> {
	constructor() {
		super('assistant/admin/company-profile');
	}

	getCompanyProfile(): Promise<CompanyProfilePayload> {
		return fetch('/api/v2/assistant/admin/company-profile')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveCompanyProfile(updates: Partial<CompanyProfile & { instructions: string }>): Promise<CompanyProfilePayload> {
		return fetch('/api/v2/assistant/admin/company-profile', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(async res => {
			if (!res.ok) throw new Error(`saveCompanyProfile ${res.status}`);
			const body = await res.json();
			return body.data;
		});
	}

	generateCompanyProfile(): Promise<CompanyProfilePayload> {
		return fetch('/api/v2/assistant/admin/company-profile/generate', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		}).then(async res => {
			if (!res.ok) throw new Error(`generateCompanyProfile ${res.status}`);
			const body = await res.json();
			return body.data;
		});
	}

	// Idempotent first-load backfill. Kicks off a background generate+save if
	// the tenant has no profile yet; no-ops once one exists. Safe to call on
	// every workspace open.
	ensureCompanyProfile(): Promise<{ status: 'exists' | 'started' | 'generating' | 'skipped' }> {
		return fetch('/api/v2/assistant/admin/company-profile/ensure', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		}).then(async res => {
			if (!res.ok) throw new Error(`ensureCompanyProfile ${res.status}`);
			const body = await res.json();
			return body.data;
		});
	}

	pageCompanyMatch(url: string): Promise<PageCompanyMatch> {
		return fetch('/api/v2/ai/page-company/match', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ url })
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pageCompanyMatch failed');
			return body.data;
		});
	}

	pageCompanyExtract(params: { url: string; title: string; text: string }): Promise<PageCompanyExtract> {
		return fetch('/api/v2/ai/page-company/extract', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pageCompanyExtract failed');
			return body.data;
		});
	}

	pagePersonExtract(params: { url: string; title: string; text: string }): Promise<PagePersonExtract> {
		return fetch('/api/v2/ai/page-person/extract', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pagePersonExtract failed');
			return body.data;
		});
	}

	pagePersonCompanies(name: string): Promise<PagePersonCompanyOption[]> {
		return fetch('/api/v2/ai/page-person/companies', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ name })
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pagePersonCompanies failed');
			return body.data.companies;
		});
	}

	pageCompanyAdd(params: {
		clientId?: number;
		prospectingId?: string;
		name?: string;
		domain?: string;
		orgNumber?: string;
		pageUrl?: string;
		overrides?: PageCompanyOverrides;
		contacts: PageCompanyContact[];
	}): Promise<PageCompanyAddResult> {
		return fetch('/api/v2/ai/page-company/add', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pageCompanyAdd failed');
			return body.data;
		});
	}
}

const resource = new AssistantCompanyProfile();

export default resource;
